import React from "react";
import toastr from "toastr";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import {
  Layout,
  Dialog,
  Button,
  Display,
  Line,
  Card,
  Column,
  Panel,
} from "ktm-ui";
import CustomToolTip from "../../components/CustomToolTip";

interface IdsBenefit {
  Id: string;
  Policy_Id: string;
  HSAAdminRate: number;
  HSAMinAdminFee: number;
  HSAMaxAdminFee: number;
  CarryFwdHSA: string;
  HasLSA: boolean;
  CarryFwdLSA: string;
}

export default KTMScreen(
  { name: "enroll.product.edit-hsa" },
  class extends React.Component<{ policyBenefit_id: string }> {
    data: {
      dsBenefit: IdsBenefit;
    };

    // Store original data for comparison
    originalData: IdsBenefit | null = null;

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsBenefit";
      await KTMApi.start(screen, {
        "@policyBenefit_id": this.props.policyBenefit_id,
      });
      screen.title = "Edit HSA Details";
      // Store a copy of the original data
      this.originalData = { ...this.data.dsBenefit };
    }

    main({ field, draw, action }: RenderEvent) {
      return (
        <Layout>
          {draw(this.header)}
          {draw(this.main_tab)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header({ field, draw, action }: RenderEvent) {
      return <Dialog.Header label="Edit HSA Details" />;
    }

    footer({ field, draw, action }: RenderEvent) {
      var isDisabled =
        this.data.dsBenefit.HSAAdminRate === 0 ||
        this.data.dsBenefit.CarryFwdHSA === "-- Select --"
          ? true
          : false;

      return (
        <Dialog.Footer>
          <Button
            action={action(this.submit)}
            label="Save"
            size="large"
            style="primary"
            disable={isDisabled}
          />
        </Dialog.Footer>
      );
    }

    details({ field, draw, action }: RenderEvent) {
      return (
        <Card>
          <Line>
            <Display
              required
              field={field("HSAAdminRate")}
              toolTip={
                <CustomToolTip
                  header="HSA Admin Rate"
                  bodyContent="Typically 10% The administration fee applied per approved claim (plus tax). The amount of tax is determined by the Tax Jurisdiction of your policy."
                />
              }
            />
            <Display
              required
              field={field("HSAMinAdminFee")}
              toolTip={
                <CustomToolTip
                  header="HSA Min Admin Fee"
                  bodyContent="Rarely used. Leave blank unless you want a minimum admin charge applied per claim."
                />
              }
            />
            <Display
              required
              field={field("HSAMaxAdminFee")}
              toolTip={
                <CustomToolTip
                  header="HSA Max Admin Fee"
                  bodyContent="Rarely used. Leave blank unless you want a maximum admin charge applied per claim."
                />
              }
            />
          </Line>
          <Display
            required
            field={field("CarryFwdHSA")}
            toolTip={
              <CustomToolTip
                header="HSA Carry Fwd"
                bodyContent="Choose between Credit, Expense or No CarryForward."
              />
            }
          />
        </Card>
      );
    }

    main_tab({ field, draw, action }: RenderEvent) {
      return (
        <Panel>
          <Column>{draw(this.details)}</Column>
        </Panel>
      );
    }

    getChangedFields(originalData, updatedData) {
        if (!originalData) return {};

        const changes = {};
        for (const key in updatedData) {
          if (updatedData[key] !== originalData[key]) {
            changes[key] = {
              oldValue:
                originalData[key] !== undefined ? originalData[key] : "N/A",
              newValue: updatedData[key] !== undefined ? updatedData[key] : "N/A",
            };
          }
        }
        return changes;
    }

    async submit({ screen }: ActionEvent) {
      if (this.data.dsBenefit.CarryFwdHSA === "No Carry Forward") {
        this.data.dsBenefit.CarryFwdHSA = null;
      }

      if (this.data.dsBenefit.HasLSA) {
        const hsaCf = this.data.dsBenefit.CarryFwdHSA;
        const lsaCf = this.data.dsBenefit.CarryFwdLSA;
        if (
          (hsaCf === "CCF" && lsaCf === "ECF") ||
          (hsaCf === "ECF" && lsaCf === "CCF")
        ) {
          toastr.error(
            "Credit Carry Forward (CCF) and Expense Carry Forward (ECF) cannot be combined on the same product.",
            "Invalid Carry Forward combination"
          );
          return;
        }
      }
      if (this.data.dsBenefit.HSAAdminRate === null) {
        this.data.dsBenefit.HSAAdminRate = 0;
      }
      if (this.data.dsBenefit.HSAMinAdminFee === null) {
        this.data.dsBenefit.HSAMinAdminFee = 0;
      }
      if (this.data.dsBenefit.HSAMaxAdminFee === null) {
        this.data.dsBenefit.HSAMaxAdminFee = 0;
      }

      // Guardrail: block obviously wrong admin fees; warn on unusual ones.
      // Typical HSA admin rate is 5–10%; 100% caused the Wild Wapiti incident.
      const rate = Number(this.data.dsBenefit.HSAAdminRate);
      if (rate > 50) {
        toastr.error(
          `HSA Admin Rate of ${rate}% exceeds the 50% maximum. Please correct before saving.`
        );
        return;
      }
      if (rate < 5 || rate > 10) {
        try {
          await screen.showWarning({
            message: `HSA Admin Rate is ${rate}%. Are you sure this is correct?`,
          });
        } catch {
          return;
        }
      }

      // Track changes
      const changes = this.getChangedFields(
          this.originalData,
          this.data.dsBenefit
      );

      const logDetails = Object.entries(changes)
          .map(([key, value]) => {
          const change = value as { oldValue: any; newValue: any };
          return `${key}: ${change.oldValue} → ${change.newValue}`;
          })
          .join(" | ");
      
      const policyLogs = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
        Note: logDetails,
        Action: "Policy Update HSA settings",
        PolicyBenefitId: this.props.policyBenefit_id,
        }),
      };

      await KTMApi.action(screen, "submit", {
        "@dsBenefit.HSAAdminRate": this.data.dsBenefit.HSAAdminRate,
        "@dsBenefit.HSAMinAdminFee": this.data.dsBenefit.HSAMinAdminFee,
        "@dsBenefit.HSAMaxAdminFee": this.data.dsBenefit.HSAMaxAdminFee,
        "@dsBenefit.CarryFwdHSA": this.data.dsBenefit.CarryFwdHSA,
        "@policyBenefit_id": this.props.policyBenefit_id,
      }).then(() => {
        fetch("/api/PlanAdmin/updatepolicylogs", policyLogs);
      });
      screen.close({ continue: true });
    }
  }
);
